import { FC, useCallback, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  LoadingOverlay,
  Alert,
  MultiSelect,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconSend,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ReportEditor, ReportEditorRef } from './ReportEditor';
import { SignatureSection } from './components/SignatureSection';
import { useReportEditor } from './hooks/useReportEditor';
import { useReportSave } from './hooks/useReportSave';
import { TiptapJSON, SignerEntry } from './types/template.types';
import { validateReport } from './utils/section-validation';
import { usePdfActions } from '../diagnostic-reports/hooks/usePdfActions';
import { ROUTES } from '@consts/routes.consts';
import styles from './ReportEditorPage.module.css';
import { formatHumanName } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';

/**
 * Page component for editing diagnostic reports with Tiptap
 * Route: /lims/order/:orderId/edit-report
 * @returns Report editor page component
 */
export const ReportEditorPage: FC = () => {
  const { orderId, reportId } = useParams<{
    orderId: string;
    reportId?: string;
  }>();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { t } = useTranslation(['lims', 'common']);

  // Report editor state
  const {
    loading: editorLoading,
    error: editorError,
    template,
    bindingData,
    savedContent,
    isReady,
    orderDetails,
  } = useReportEditor({
    orderId,
    templateId: 'neurocutaneous-syndromes',
    reportId,
  });

  // Content state
  const [editorContent, setEditorContent] = useState<TiptapJSON | undefined>(
    undefined
  );
  const [signers, setSigners] = useState<SignerEntry[]>([
    { id: 'signer-0', name: '', title: '' },
    { id: 'signer-1', name: '', title: '' },
  ]);

  // Reviewers state
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);
  const [availableReviewers, setAvailableReviewers] = useState<Practitioner[]>(
    []
  );

  const editorRef = useRef<ReportEditorRef>(null);

  // Report save hook
  const {
    saveDraft,
    submitForReview,
    loadReportContent,
    saving,
    error: saveError,
  } = useReportSave({
    serviceRequest: orderDetails.serviceRequest,
    patient: orderDetails.patient,
    specimens: orderDetails.specimens,
    templateId: 'neurocutaneous-syndromes',
    templateVersion: template?.version || '1.0.0',
    onSaveSuccess: () => {
      // Navigate back to order details after successful save
      // Pass state to trigger reload of reports
      if (orderId) {
        navigate(ROUTES.LIMS.ORDER_DETAILS(orderId), {
          state: { reloadReports: true },
        });
      }
    },
  });

  const { pdfLoading, handlePreviewPDF, handleExportPDF, handlePrint } =
    usePdfActions({
      editorRef,
      template,
      content: editorContent,
      orderId,
      signers,
      bindingData,
    });

  // Load available reviewers
  const loadAvailableReviewers = useCallback(async () => {
    try {
      const practitioners = await medplum.searchResources('Practitioner', {
        _sort: 'name',
      });
      setAvailableReviewers(practitioners || []);
    } catch (error) {
      console.error('Error loading available reviewers:', error);
    }
  }, [medplum]);

  // Load reviewers on mount
  useEffect(() => {
    loadAvailableReviewers();
  }, [loadAvailableReviewers]);

  // Load saved signers when editing an existing report
  useEffect(() => {
    if (!reportId) {
      return;
    }
    loadReportContent(reportId).then((result) => {
      if (result?.signers && result.signers.length > 0) {
        setSigners(result.signers);
      }
    });
  }, [reportId, loadReportContent]);

  // Handle content changes from editor
  const handleContentChange = useCallback((content: TiptapJSON) => {
    setEditorContent(content);
  }, []);

  // Handle signer list changes
  const handleSignersChange = useCallback((newSigners: SignerEntry[]) => {
    setSigners(newSigners);
  }, []);

  // Validate and get validation results
  const getValidationResults = useCallback(() => {
    if (!template || !editorContent) {
      return null;
    }

    return validateReport(template, editorContent);
  }, [template, editorContent]);

  // Handle save draft
  const handleSaveDraft = useCallback(async () => {
    if (!editorContent) {
      return;
    }

    await saveDraft(editorContent, reportId, {
      signers,
    });
  }, [editorContent, signers, reportId, saveDraft]);

  // Handle submit for review
  const handleSubmitForReview = useCallback(async () => {
    if (!editorContent || !template) {
      return;
    }

    // Validate before submitting
    const validation = validateReport(template, editorContent);
    if (!validation.isValid) {
      return;
    }

    // Submit the report with reviewers (hook will validate and create tasks)
    await submitForReview(editorContent, selectedReviewers, {
      signers,
    });
  }, [editorContent, template, selectedReviewers, signers, submitForReview]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (orderId) {
      navigate(ROUTES.LIMS.ORDER_DETAILS(orderId));
    } else {
      navigate(ROUTES.LIMS.ROOT);
    }
  }, [navigate, orderId]);

  // Loading state
  if (editorLoading) {
    return (
      <Container size="xl" py="xl">
        <Paper p="xl" pos="relative" mih={400}>
          <LoadingOverlay visible />
        </Paper>
      </Container>
    );
  }

  // Error state
  if (editorError) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('reportEditor.error', { ns: 'lims' })}
          color="red"
        >
          {editorError}
        </Alert>
        <Button
          mt="md"
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBack}
        >
          {t('buttons.back', { ns: 'common' })}
        </Button>
      </Container>
    );
  }

  // Not ready state
  if (!isReady || !template) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('reportEditor.dataNotReady', { ns: 'lims' })}
          color="yellow"
        >
          {t('reportEditor.waitingForData', { ns: 'lims' })}
        </Alert>
      </Container>
    );
  }

  const validationResults = getValidationResults();

  return (
    <Container size="xl" py="md" className={styles.pageContainer}>
      <Stack gap="md">
        {/* Header */}
        <Paper p="md" withBorder>
          <Group justify="space-between" wrap="wrap">
            <Group gap="md">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={handleBack}
              >
                {t('buttons.back', { ns: 'common' })}
              </Button>
              <Title order={2}>{t('reportEditor.title', { ns: 'lims' })}</Title>
            </Group>

            <Button
              variant="outline"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSaveDraft}
              loading={saving}
              disabled={!editorContent}
            >
              {t('reportEditor.saveDraft', { ns: 'lims' })}
            </Button>
          </Group>
        </Paper>

        {/* Error message */}
        {saveError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {saveError}
          </Alert>
        )}

        {/* Main content */}
        {/* Report Editor */}
        <ReportEditor
          ref={editorRef}
          template={template}
          initialContent={savedContent}
          bindingData={bindingData}
          onContentChange={handleContentChange}
          loading={editorLoading}
          onPreviewPDF={handlePreviewPDF}
          onExportPDF={handleExportPDF}
          onPrint={handlePrint}
          pdfLoading={pdfLoading}
        />

        {/* Signature Section */}
        <SignatureSection signers={signers} onChange={handleSignersChange} />

        <Group>
          <MultiSelect
            label={t('orderDetails.selectReviewers', { ns: 'lims' })}
            placeholder={t('orderDetails.chooseReviewers', {
              ns: 'lims',
            })}
            value={selectedReviewers}
            onChange={setSelectedReviewers}
            data={availableReviewers.map((reviewer) => ({
              value: reviewer.id || '',
              label:
                formatHumanName(reviewer?.name?.[0]) ||
                `${t('orderDetails.practitioner', { ns: 'lims' })} ${
                  reviewer.id
                }`,
            }))}
            withAsterisk
            searchable
            clearable
            flex={1}
          />{' '}
          <Button
            onClick={handleSubmitForReview}
            loading={saving}
            disabled={!editorContent || !validationResults?.isValid}
            variant="light"
            leftSection={<IconSend size={16} />}
            mt="lg"
          >
            {t('reportEditor.submitForReview', { ns: 'lims' })}
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default ReportEditorPage;
